'use client';
import { useState, useCallback, useEffect } from 'react';

export type ScreenSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

type WindowDimensions = {
  isMobile?: boolean;
  screenSize: ScreenSize;
  width?: number;
  height?: number;
};

export function useWindowDimensions(): WindowDimensions {
  const widthToScreensize = useCallback((width: number): ScreenSize => {
    // since screen sizes are dynamic over the size of the rem unit, we get it here
    // to match css breakpoints
    const remSize = parseFloat(getComputedStyle(document.documentElement).fontSize);

    if (width < remSize * 48) return 'xs';
    else if (width < remSize * 67.5) return 'sm';
    else if (width < remSize * 80) return 'md';
    else if (width < remSize * 90) return 'lg';
    else if (width < remSize * 98.5) return 'xl';
    else return 'xxl';
  }, []);

  const getWindowDimensions = useCallback(
    () => ({
      height: typeof window === 'undefined' ? undefined : window?.innerHeight,
      width: typeof window === 'undefined' ? undefined : window?.innerWidth,
      screenSize: typeof window === 'undefined' ? 'xs' : widthToScreensize(window?.innerWidth),
      isMobile:
        typeof window === 'object' && typeof window?.innerWidth === 'number'
          ? window.innerWidth < 768
          : true,
    }),
    [],
  );

  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions);

  const handleResize = useCallback(
    () => setWindowDimensions(getWindowDimensions()),
    [getWindowDimensions],
  );

  useEffect(() => {
    // Trigger on first client-side render
    handleResize();

    window?.addEventListener('resize', handleResize);
    return () => window?.removeEventListener('resize', handleResize);
  }, [handleResize]);

  return windowDimensions;
}
